'use client';

import { CloseRounded } from '@mui/icons-material';
import { Box, Dialog, IconButton, Skeleton, Slide, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';

import type { ZoneGroup } from '@/components/homepage/groupByZone';

import { DestinationMenuCard } from './DestinationMenuCard';

import type { TransitionProps } from '@mui/material/transitions';

const SlideUp = forwardRef<HTMLElement, TransitionProps & { children: React.ReactElement }>(
    function SlideUp(props, ref) {
        return <Slide direction="up" ref={ref} {...props} />;
    },
);

type Props = {
    open: boolean;
    groups: ZoneGroup[];
    loading: boolean;
    isError?: boolean;
    onClose: () => void;
    onNavigate: () => void;
};

export function DestinationsOverlay({
    open,
    groups,
    loading,
    isError,
    onClose,
    onNavigate,
}: Props) {
    const t = useTranslations('nav');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            slots={{ transition: SlideUp }}
            slotProps={{ paper: { sx: { backgroundColor: 'background.default' } } }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    backgroundColor: 'background.default',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Typography variant="h4" component="h2" sx={{ color: 'text.primary' }}>
                    {t('destinations')}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseRounded />
                </IconButton>
            </Box>

            {/* Content — scroll vertical, sections par zone */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {loading ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Skeleton key={i} variant="rounded" height={90} />
                        ))}
                    </Box>
                ) : isError ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'error.main', fontSize: 14 }}>
                        {t('destinationsError')}
                    </Box>
                ) : groups.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary', fontSize: 14 }}>
                        {t('destinationsEmpty')}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {groups.map((group) => (
                            <Box key={group.slug}>
                                <Typography
                                    variant="overline"
                                    component="h3"
                                    sx={{
                                        color: 'text.secondary',
                                        letterSpacing: '0.08em',
                                        display: 'block',
                                        mb: 1.5,
                                    }}
                                >
                                    {group.label}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 1.25,
                                    }}
                                >
                                    {group.tiles.map((tile) => (
                                        <DestinationMenuCard
                                            key={tile.id}
                                            tile={tile}
                                            onNavigate={onNavigate}
                                            triggerContext="mobile_drawer"
                                            imageHeight={80}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Dialog>
    );
}
